import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { isValidCnpjAlfa, normalizeCnpj } from '../../../../utils/cnpj.validator';

import { Types } from '../../../../../domain/types.domain';
import {
  InputComponent,
  ValidationErrorModalComponent,
  ValidationErrorData,
} from '../../../../../@ads/components';
import { ValidateCpfEmailServiceInstruction } from '../../../../../builder/instructions/services/validate-cpf-email.service.instruction';
import { SearchCnpjProviderInstruction } from '../../../../../builder/instructions/providers/search-cnpj.provider.instruction';
import { CreateAccountPjFormHandler } from '../../create-account-pj-form.handler';
import { AccountFormHandler } from '../../../create-account/account-form.handler';
import { strictEmailValidator } from '../../../../utils/email.validator';

function cnpjFormatValidator(control: FormControl): { [key: string]: any } | null {
  const raw = normalizeCnpj(control.value);
  if (!raw) return null;
  return isValidCnpjAlfa(raw) ? null : { cnpjInvalid: true };
}

function emailMatchValidator(group: FormGroup): { [key: string]: any } | null {
  const email = group.get('email')?.value;
  const confirmacao = group.get('emailConfirmacao')?.value;
  if (email && confirmacao && email !== confirmacao) {
    return { emailMismatch: true };
  }
  return null;
}

@Component({
  selector: 'app-company-form',
  standalone: true,
  imports: [ReactiveFormsModule, InputComponent, ValidationErrorModalComponent],
  providers: [
    new ValidateCpfEmailServiceInstruction(),
    ...SearchCnpjProviderInstruction.provide(),
  ],
  templateUrl: './company-form.component.html',
  styleUrl: './company-form.component.scss',
})
export class CompanyFormComponent implements OnInit, OnDestroy {
  private readonly _handler = inject(CreateAccountPjFormHandler);
  private readonly _destroy$ = new Subject<void>();
  private readonly _accountFormHandler = inject(AccountFormHandler);
  private readonly _validateCpfEmailService = inject(Types.ValidateCpfEmailService);
  private readonly _searchCnpjProvider = inject(Types.SearchCnpjProvider);

  private _lastSearchedCnpj = '';

  @Input() cityConnection: string = '';
  /** Razão social e CNPJ vieram verificados do gov.br e não podem ser editados (cadastro curto, `company-registration`). */
  @Input() lockedIdentity = false;
  @Input() cityId: number | undefined;

  @Output() setStepValidation = new EventEmitter<boolean>();

  public validationError = signal<ValidationErrorData | null>(null);
  public validating = signal(false);
  public loadingCnpj = signal(false);

  public form = new FormGroup(
    {
      razaoSocial: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)],
      }),
      nomeFantasia: new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(255)],
      }),
      cnpj: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, cnpjFormatValidator as any],
      }),
      telefone: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(20)],
      }),
      email: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, strictEmailValidator],
      }),
      emailConfirmacao: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, strictEmailValidator],
      }),
    },
    { validators: emailMatchValidator as any }
  );

  public readonly errorMessages = {
    required: 'Campo obrigatório',
    email: 'E-mail inválido',
    cnpjInvalid: 'CNPJ inválido',
    maxlength: 'Tamanho máximo excedido',
    emailMismatch: 'Os e-mails não coincidem',
    emailExists: 'E-mail já cadastrado',
    cnpjExists: 'CNPJ já cadastrado',
  };

  ngOnInit(): void {
    const saved = this._handler.getCompanyInfo();
    if (saved) {
      this.form.patchValue(saved);
    }

    this.form.controls.cnpj.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      // CNPJ alfanumérico é sempre em maiúsculas.
      if (typeof value === 'string') {
        const upper = value.toUpperCase();
        if (value !== upper) {
          this.form.controls.cnpj.setValue(upper, { emitEvent: false });
        }
      }
    });

    this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.setStepValidation.emit(this.form.valid);
      if (this.form.valid) {
        const raw = this.form.getRawValue();
        this._handler.setCompanyInfo({
          ...raw,
          cnpj: normalizeCnpj(raw.cnpj),
        });
      }
    });

    this.setStepValidation.emit(this.form.valid);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  async validateEmailAndCnpj(): Promise<boolean> {
    if (this.validating()) return false;

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return false;
    }

    const cnpjValue = normalizeCnpj(this.form.controls.cnpj.value);
    const emailValue = this.form.controls.email.value;

    if (!cnpjValue || !emailValue || !this.cityConnection) {
      return true;
    }

    this.validating.set(true);

    try {
      const result = await this._validateCpfEmailService.handle({
        cpf: cnpjValue,
        email: emailValue,
        connection: this.cityConnection,
        cityId: this.cityId,
        accountType: 'EXTERNAL_USER',
      });

      this.validating.set(false);

      if (result.emailExists && result.cpfExists) {
        this.form.controls.email.setErrors({ emailExists: true });
        this.form.controls.cnpj.setErrors({ cnpjExists: true });
        this.form.controls.email.markAsTouched();
        this.form.controls.cnpj.markAsTouched();
        this.validationError.set({ type: 'cnpj_email_exists' });
        return false;
      }

      if (result.emailExists) {
        this.form.controls.email.setErrors({ emailExists: true });
        this.form.controls.email.markAsTouched();
        this.validationError.set({ type: 'email' });
        return false;
      }

      if (result.cpfExists) {
        this.form.controls.cnpj.setErrors({ cnpjExists: true });
        this.form.controls.cnpj.markAsTouched();
        this.validationError.set({ type: 'cnpj' });
        return false;
      }

      return true;
    } catch {
      this.validating.set(false);
      return true;
    }
  }

  closeValidationError(): void {
    this.validationError.set(null);
  }

  async onCnpjBlur(): Promise<void> {
    const cnpjValue = normalizeCnpj(this.form.controls.cnpj.value);

    if (!isValidCnpjAlfa(cnpjValue)) {
      return;
    }

    if (cnpjValue === this._lastSearchedCnpj) {
      return;
    }

    this._lastSearchedCnpj = cnpjValue;
    this.loadingCnpj.set(true);
    this.setStepValidation.emit(false);

    try {
      const result = await this._searchCnpjProvider.handle(cnpjValue);

      if (result.error) {
        this.loadingCnpj.set(false);
        return;
      }

      // A situação cadastral vem da BrasilAPI, que serve um dump defasado da
      // Receita Federal; por isso não é usada como gate — o CNPJ apenas
      // autopreenche os dados da empresa.
      this.form.controls.razaoSocial.setValue(result.razaoSocial || '');
      this.form.controls.nomeFantasia.setValue(result.nomeFantasia || '');

      if (result.telefone && !this.form.controls.telefone.value) {
        const telefoneFormatado = result.telefone.replace(/\D/g, '');
        this.form.controls.telefone.setValue(telefoneFormatado);
      }

      if (result.email && !this.form.controls.email.value) {
        this.form.controls.email.setValue(result.email);
        this.form.controls.emailConfirmacao.setValue(result.email);
      }

      if (result.endereco) {
        this._accountFormHandler.setAddressInfo({
          cep: result.endereco.cep || '',
          rua: result.endereco.logradouro || '',
          numero: result.endereco.numero || '',
          complemento: result.endereco.complemento || '',
          bairro: result.endereco.bairro || '',
          cidade: result.endereco.cidade || '',
          estado: result.endereco.estado || '',
        });
      } else {
        // New CNPJ has no address data — clear any stale address from previous CNPJ
        this._accountFormHandler.setAddressInfo({
          cep: '',
          rua: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: '',
          estado: '',
        });
      }
    } catch {
      // silently fail
    } finally {
      this.loadingCnpj.set(false);
      this.setStepValidation.emit(this.form.valid);
    }
  }
}
